import {
  TerminalSquare, Folder, MessageSquarePlus, Settings, Globe,
  Calculator, PlaySquare, Bell, Upload, Download,
} from 'lucide-react';
import { useStore } from '../../store/useStore';
import { Clock } from './Clock';
import type { AppType } from '../../types';

const ICONS: Record<string, React.ReactNode> = {
  terminal: <TerminalSquare className="w-5 h-5 text-cyan-400" />,
  'file-manager': <Folder className="w-5 h-5 text-amber-400" />,
  projects: <Folder className="w-5 h-5 text-purple-400" />,
  editor: <Folder className="w-5 h-5 text-green-400" />,
  chat: <MessageSquarePlus className="w-5 h-5 text-blue-400" />,
  settings: <Settings className="w-5 h-5 text-slate-300" />,
  browser: <Globe className="w-5 h-5 text-pink-400" />,
  preview: <PlaySquare className="w-5 h-5 text-emerald-400" />,
  calculator: <Calculator className="w-5 h-5 text-orange-400" />,
  minesweeper: <Folder className="w-5 h-5 text-red-400" />,
};

const TITLES: Record<string, string> = {
  terminal: 'Terminal',
  'file-manager': 'Files',
  projects: 'Projects',
  editor: 'Editor',
  chat: 'AI Assistant',
  settings: 'Settings',
  browser: 'Browser',
  preview: 'Preview',
  calculator: 'Calculator',
  minesweeper: 'Minesweeper',
};

const PINNED: AppType[] = ['terminal', 'file-manager', 'browser', 'calculator', 'settings'];

function StartLogo() {
  return (
    <div className="grid grid-cols-2 gap-[2.5px] w-[18px] h-[18px]">
      <div className="rounded-[1.5px] bg-gradient-to-br from-[#00adef] to-[#0078d4]" />
      <div className="rounded-[1.5px] bg-gradient-to-br from-[#00adef] to-[#0078d4]" />
      <div className="rounded-[1.5px] bg-gradient-to-br from-[#00adef] to-[#0078d4]" />
      <div className="rounded-[1.5px] bg-gradient-to-br from-[#00adef] to-[#0078d4]" />
    </div>
  );
}

export function Taskbar() {
  const windows = useStore((s) => s.windows);
  const activeWindowId = useStore((s) => s.activeWindowId);
  const focusWindow = useStore((s) => s.focusWindow);
  const minimizeWindow = useStore((s) => s.minimizeWindow);
  const appLauncherOpen = useStore((s) => s.appLauncherOpen);
  const toggleAppLauncher = useStore((s) => s.toggleAppLauncher);
  const openApp = useStore((s) => s.openApp);
  const currentProject = useStore((s) => s.currentProject);
  const syncProject = useStore((s) => s.syncProject);
  const toggleCommandPalette = useStore((s) => s.toggleCommandPalette);
  const hasGoogleAccess = useStore((s) => s.hasGoogleAccess);

  const handleWindowClick = (id: string) => {
    const win = windows.find((w) => w.id === id);
    if (activeWindowId === id && !win?.isMinimized) {
      minimizeWindow(id);
    } else {
      focusWindow(id);
    }
  };

  const runningIds = new Set(windows.map((w) => w.appType));

  return (
    <div className="h-12 shrink-0 glass border-t border-desktop-border flex items-center px-2 gap-1 z-[5000] relative">
      {/* Left: current project */}
      <div className="flex-1 min-w-0 hidden sm:flex items-center">
        {currentProject && (
          <span className="text-[11px] text-desktop-text/50 truncate px-2">
            {currentProject.name}
          </span>
        )}
      </div>

      {/* Center: Start + pinned + running (Windows 11 style) */}
      <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1 max-w-[70vw] overflow-x-auto">
        <button
          onClick={toggleAppLauncher}
          className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors shrink-0 ${
            appLauncherOpen ? 'bg-desktop-hover' : 'hover:bg-desktop-hover'
          }`}
          title="Start"
        >
          <StartLogo />
        </button>

        {PINNED.map((app) => {
          const running = runningIds.has(app);
          const activeWin = windows.find((w) => w.appType === app && w.id === activeWindowId);
          return (
            <button
              key={app}
              onClick={() => openApp(app)}
              className={`relative w-10 h-10 flex items-center justify-center rounded-lg transition-colors shrink-0 ${
                activeWin ? 'bg-desktop-hover' : 'hover:bg-desktop-hover'
              }`}
              title={TITLES[app]}
            >
              {ICONS[app]}
              {running && (
                <span
                  className={`absolute bottom-1 h-[2.5px] rounded-full transition-all ${
                    activeWin ? 'w-4 bg-desktop-accent' : 'w-1.5 bg-desktop-text/40'
                  }`}
                />
              )}
            </button>
          );
        })}

        {windows
          .filter((w) => !(PINNED as string[]).includes(w.appType))
          .map((win) => (
            <button
              key={win.id}
              onClick={() => handleWindowClick(win.id)}
              className={`relative w-10 h-10 flex items-center justify-center rounded-lg transition-colors shrink-0 ${
                activeWindowId === win.id ? 'bg-desktop-hover' : 'hover:bg-desktop-hover'
              }`}
              title={win.title}
            >
              {ICONS[win.appType]}
              <span
                className={`absolute bottom-1 h-[2.5px] rounded-full transition-all ${
                  activeWindowId === win.id ? 'w-4 bg-desktop-accent' : 'w-1.5 bg-desktop-text/40'
                }`}
              />
            </button>
          ))}
      </div>

      {/* Right: tray */}
      <div className="flex-1 flex items-center gap-1 justify-end shrink-0">
        {currentProject && hasGoogleAccess && (
          <div className="hidden sm:flex items-center gap-1 mr-1">
            <button
              onClick={() => syncProject(currentProject.id, 'down')}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-desktop-hover text-desktop-text/60 hover:text-desktop-text transition-colors"
              title="Pull from Google Drive"
            >
              <Download className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => syncProject(currentProject.id, 'up')}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-desktop-hover text-desktop-text/60 hover:text-desktop-text transition-colors"
              title="Push to Google Drive"
            >
              <Upload className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <button
          onClick={toggleCommandPalette}
          className="w-9 h-9 hidden sm:flex items-center justify-center rounded-lg hover:bg-desktop-hover transition-colors"
          title="Command Palette (Ctrl+K)"
        >
          <Bell className="w-4 h-4 text-desktop-text/60" />
        </button>
        <Clock />
      </div>
    </div>
  );
}
